import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { Box, Typography } from "@mui/material";

export interface EvaluationChartProps {
  evaluations: any[];
  exerciseTitles: { [key: string]: string | number };
  exerciseIdToCategory: { [key: string]: number };
  categoryIdToName: { [key: number]: string };
  specialRadarData?: {
    subject: string;
    score: number;
    fullMark: number;
    isSpecial: boolean;
  }[];
}

export default function EvaluationChart({
  evaluations,
  exerciseTitles,
  exerciseIdToCategory,
  categoryIdToName,
  specialRadarData = []
}: EvaluationChartProps) {
  if (!evaluations.length) return <Typography>Aucune évaluation</Typography>;

  // ➤ Calcul du taux global
let totalGlobal = 0;
let countGlobal = 0;

evaluations.forEach((ev) => {
  const scores: number[] = JSON.parse(ev.details || "[]");
  const totalScore = scores.reduce((a, b) => a + b, 0);

  const keyBase = ev.exercise_id.toString();
  const maxPoints = Number(exerciseTitles[`${keyBase}_max_points`] || 0);
  const repetitions = Number(exerciseTitles[`${keyBase}_repetitions`] || 0);
  const maxPossible = maxPoints * repetitions;

  if (maxPossible > 0) {
    totalGlobal += (totalScore / maxPossible) * 100;
    countGlobal++;
  }
});

const globalRate = countGlobal > 0 ? (totalGlobal / countGlobal).toFixed(2) : "0";

const getGlobalRateColor = (rate: number) => {
  if (rate < 40) return "#ff9800";       // Rouge
  if (rate < 70) return "#e5bf03";       // Orange
  return "#09de21";                      // Vert
};

const getMotivationMessage = (rate: number) => {
  if (rate < 40)
    return "🎧 Ecoutes bien les conseils, reste tjrs concentré(e) et motivé(e) ! Chaque entraînement t’aide à progresser, continue tes efforts 👍";

  if (rate < 70)
    return "💪 Beau travail ! Tu es sur la bonne voie, encore un peu d’effort et tu vas vraiment gagner en maîtrise 🙂";

  return "🔥 Excellent travail ! Ta maîtrise est solide, continue à performer comme ça ! 🚀";
};



  // Agrégation des scores par catégorie
  const categoryAggregates: {
    [catId: number]: { total: number; count: number };
  } = {};

  evaluations.forEach((evalItem) => {
    const scores: number[] = JSON.parse(evalItem.details || "[]");
    const totalScore = scores.reduce((a, b) => a + b, 0);
    const keyBase = evalItem.exercise_id.toString();
    const maxPoints = Number(exerciseTitles[`${keyBase}_max_points`] as unknown) || 0;
    const repetitions = Number(exerciseTitles[`${keyBase}_repetitions`] as unknown) || 0;
    const maxPossibleScore = maxPoints * repetitions;

    const successRate =
      maxPossibleScore > 0
        ? parseFloat(((totalScore / maxPossibleScore) * 100).toFixed(2))
        : 0;

    const catId = exerciseIdToCategory[keyBase];
    if (catId != null) {
      if (!categoryAggregates[catId]) {
        categoryAggregates[catId] = { total: 0, count: 0 };
      }
      categoryAggregates[catId].total += successRate;
      categoryAggregates[catId].count += 1;
    }
  });

  // Formatage pour Recharts
  const data = Object.entries(categoryAggregates).map(([catIdStr, { total, count }]) => {
    const catId = parseInt(catIdStr, 10);
    const avg = count > 0 ? parseFloat((total / count).toFixed(2)) : 0;
    return {
      subject: categoryIdToName[catId] || `Catégorie ${catId}`,
      score: avg,
      fullMark: 100,
    };
  });

  const radarData = [...data, ...specialRadarData];
  const globalRateColor = getGlobalRateColor(Number(globalRate));


  return (
    <Box sx={{ width: "100%", height: 400 }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={radarData}>
          <PolarGrid />
          <PolarAngleAxis dataKey="subject" />
          <PolarRadiusAxis angle={30} domain={[0, 100]} />
          <Tooltip
            formatter={(value: any) => `${value} %`}
            labelFormatter={(label) => `Catégorie : ${label}`}
          />
          <Radar
            name="Score moyen"
            dataKey="score"
            stroke="#8884d8"
            fill="#8884d8"
            fillOpacity={0.6}
          />
        </RadarChart>
      </ResponsiveContainer>
      <Box
          sx={{
          textAlign: "center",
          marginBottom: 2
        }}
      >
        {Number(globalRate) > 55 && (
          <span
            style={{
              display: "inline-block",
              padding: "6px 14px",
              borderRadius: "8px",
              background: globalRateColor,
              color: "white",
              fontWeight: "bold",
              fontSize: "1rem",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
            }}
          >
            Taux de réussite global : {globalRate} %
          </span>
        )}
        <Typography
          sx={{
            marginTop: 1,
            textAlign: "center",
            fontFamily: "'Caveat', cursive, sans-serif",
            fontSize: "1.8rem",
            fontWeight: 500,
            fontStyle: "italic",
            color: globalRateColor
          }}
        >
          {getMotivationMessage(Number(globalRate))}
        </Typography>
      </Box>  
    </Box>
  );
}
